import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { SignInButton, SignedIn, SignedOut } from '@clerk/clerk-react';
import { useAuth } from '@clerk/clerk-react';
import { api } from '../utils/api';

/**
 * Vendor pickup view: opened when seller scans customer's QR code.
 * URL: /pickup?t={pickupToken}
 * Only the vendor who sold the items can see this order and mark items picked up.
 * Requires sign-in; if not signed in or wrong seller, shows prompt to sign in and scan again.
 */
export default function Pickup() {
  const { getToken, isSignedIn, isLoaded: authLoaded } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('t');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authError, setAuthError] = useState(null); // 401 / 403 / 404 message for sign-in or wrong vendor
  const [data, setData] = useState(null);
  const [markingId, setMarkingId] = useState(null);

  useEffect(() => {
    if (!token || token.trim() === '') {
      setError('Invalid pickup link. Show the QR code from your purchase to the vendor.');
      setLoading(false);
      return;
    }

    if (!authLoaded) {
      return;
    }

    if (!isSignedIn) {
      setLoading(false);
      setData(null);
      setError(null);
      setAuthError('Only the vendor who sold the items can confirm pickup. Sign in to your vendor account, then scan the customer\'s QR code again.');
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setAuthError(null);
      try {
        const res = await api(`/api/pickup?t=${encodeURIComponent(token)}`, { getToken });
        if (res.status === 401) {
          const body = await res.json().catch(() => ({}));
          if (!cancelled) setAuthError(body.error || 'Sign in to your vendor account, then scan the customer\'s QR code again.');
          setLoading(false);
          return;
        }
        if (res.status === 403) {
          const body = await res.json().catch(() => ({}));
          if (!cancelled) setAuthError(body.error || 'vendor account required. Sign in with the vendor account that sold the items.');
          setLoading(false);
          return;
        }
        if (res.status === 404) {
          const body = await res.json().catch(() => ({}));
          if (!cancelled) setAuthError(body.error || 'No items from your vendor account in this receipt. Only the vendor who sold the items can mark them as picked up.');
          setLoading(false);
          return;
        }
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          setError(body.error || `Could not load pickup (${res.status})`);
          setLoading(false);
          return;
        }
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load pickup.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, getToken, isSignedIn, authLoaded]);

  const handleMarkPickedUp = async (saleId) => {
    if (!data || !data.sales) return;
    setMarkingId(saleId);
    setError(null);
    try {
      const res = await api('/api/pickup/mark-picked-up', {
        getToken,
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ saleId, token }),
      });
      if (res.status === 401 || res.status === 403) {
        const body = await res.json().catch(() => ({}));
        setAuthError(body.error || 'Sign in to your vendor account and scan the QR code again.');
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed to mark picked up (${res.status})`);
      }
      const updated = await res.json();
      const nextSales = data.sales.map((s) => (s.id === saleId ? { ...s, status: updated.status } : s));
      setData((prev) => ({ ...prev, sales: nextSales }));
      const allPickedUp = nextSales.every((s) => s.status === 'PickedUp');
      if (allPickedUp) navigate('/', { replace: true });
    } catch (err) {
      setError(err.message || 'Failed to mark as picked up.');
    } finally {
      setMarkingId(null);
    }
  };

  if (loading) {
    return (
      <div className="max-w-lg mx-auto p-6 flex items-center justify-center min-h-[40vh]">
        <p className="text-xl text-slate-500 dark:text-slate-400">Loading pickup…</p>
      </div>
    );
  }

  // Require sign-in as the correct vendor; show prompt to sign in and scan again
  if (authError && (!data || !data.sales)) {
    return (
      <div className="max-w-lg mx-auto p-6">
        <h1 className="text-2xl font-bold mb-4">Vendor pickup</h1>
        <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-200 dark:border-amber-700 p-6">
          <p className="text-amber-900 dark:text-amber-100 font-medium mb-4">{authError}</p>
          <p className="text-slate-700 dark:text-slate-300 text-sm mb-4">
            Only the vendor who sold the items can view this order and mark pickup. Sign in to your vendor account, then scan the customer’s QR code again.
          </p>
          <SignedOut>
            <SignInButton mode="modal">
              <button
                type="button"
                className="w-full py-3 rounded-xl font-semibold bg-indigo-600 text-white hover:bg-indigo-700 active:bg-indigo-800 transition-colors"
              >
                Sign in to vendor account
              </button>
            </SignInButton>
          </SignedOut>
          <SignedIn>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              You’re signed in but this receipt has no items from your vendor account. Sign in with the correct vendor account and scan the QR code again.
            </p>
          </SignedIn>
        </div>
      </div>
    );
  }

  if (error && (!data || !data.sales)) {
    return (
      <div className="max-w-lg mx-auto p-6">
        <h1 className="text-2xl font-bold mb-4">Pickup</h1>
        <div className="rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4">
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
      </div>
    );
  }

  // Only render receipt and list when we have valid data (avoids "Cannot read properties of null (reading 'sales')")
  if (!data || !data.sales) {
    return (
      <div className="max-w-lg mx-auto p-6 flex items-center justify-center min-h-[40vh]">
        <p className="text-xl text-slate-500 dark:text-slate-400">Loading pickup…</p>
      </div>
    );
  }

  const pendingCount = data.sales.filter((s) => s.status !== 'PickedUp').length;
  const allDone = pendingCount === 0;

  return (
    <div className="max-w-lg mx-auto p-6">
      <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600 dark:text-indigo-400 mb-1">
        Vendor scan
      </p>
      <h1 className="text-2xl font-bold mb-2">Confirm customer pickup</h1>
      <p className="text-slate-600 dark:text-slate-400 mb-6">
        Hand each item to the customer, then tap Mark picked up. When everything is done you&apos;ll return home automatically.
      </p>

      {error && (
        <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4 mb-4">
          <p className="text-amber-800 dark:text-amber-200">{error}</p>
        </div>
      )}

      {allDone && (
        <div className="rounded-xl bg-green-50 dark:bg-green-900/20 border-2 border-green-300 dark:border-green-700 p-4 mb-4 text-center">
          <p className="font-semibold text-green-800 dark:text-green-200 text-lg">All items picked up</p>
          <p className="text-sm text-green-700 dark:text-green-300 mt-1">This order is complete.</p>
        </div>
      )}

      <div className="rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 overflow-hidden">
        <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center gap-2">
          <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
            {data.sales.length} item{data.sales.length !== 1 ? 's' : ''}
          </span>
          {pendingCount > 0 ? (
            <span className="px-3 py-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 text-sm font-medium">
              {pendingCount} still pending
            </span>
          ) : (
            <span className="px-3 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 text-sm font-medium">
              All picked up
            </span>
          )}
        </div>
        <ul className="divide-y divide-slate-200 dark:divide-slate-700">
          {data.sales.map((sale) => (
            <li key={sale.id} className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-lg">{sale.itemTitle}</p>
                <p className="text-slate-600 dark:text-slate-400">
                  ${parseFloat(sale.amount).toFixed(2)}
                  {sale.status === 'PickedUp' && (
                    <span className="ml-2 px-2 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 text-sm">
                      Picked up
                    </span>
                  )}
                </p>
              </div>
              {sale.status !== 'PickedUp' && (
                <button
                  type="button"
                  onClick={() => handleMarkPickedUp(sale.id)}
                  disabled={markingId === sale.id}
                  className="shrink-0 h-14 px-6 rounded-xl font-semibold text-lg bg-green-600 text-white hover:bg-green-700 active:bg-green-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-[0_4px_0_0_#15803d] active:shadow-none active:translate-y-1"
                >
                  {markingId === sale.id ? 'Marking…' : 'Mark picked up'}
                </button>
              )}
            </li>
          ))}
        </ul>
        <div className="p-5 border-t-2 border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80 flex justify-between items-center">
          <span className="text-lg font-semibold text-slate-700 dark:text-slate-300">Order total</span>
          <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">${parseFloat(data.totalAmount).toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
}
